<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useConversationLead } from 'dashboard/composables/useConversationLead';
import CreateLeadDialog from 'dashboard/components-next/Crm/Conversation/CreateLeadDialog.vue';
import LeadQuickActionsMenu from 'dashboard/components-next/Crm/Conversation/LeadQuickActionsMenu.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// CRM - badge "Lead" quando há lead atrelado à conversa (gated pela flag)
const conversationIdRef = computed(() => props.chat?.id);
const { lead: linkedLead, isCrmEnabled: isCrmFlagEnabled, refresh: refreshLead } =
  useConversationLead(conversationIdRef);

const createLeadDialogRef = ref(null);
function openCreateLead() { createLeadDialogRef.value?.open(); }
function onLeadCreated(lead) {
  // Atualiza o badge imediatamente sem esperar nova fetch
  if (lead) linkedLead.value = lead;
  refreshLead();
}
function onLeadUpdated(lead) {
  if (lead) linkedLead.value = lead;
}

const leadHref = computed(() => {
  const l = linkedLead.value;
  if (!l) return null;
  const aid = accountId.value;
  return l.pipeline?.id
    ? `/app/accounts/${aid}/pipelines/${l.pipeline.id}?lead=${l.id}`
    : `/app/accounts/${aid}/pipelines`;
});
</script>

<template>
  <div
    ref="conversationHeader"
    class="relative flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <!-- Indicador do lead - centralizado no header, badge sem fundo,
         detalhe cinza, formato link (só no layout em linha do desktop). -->
    <router-link
      v-if="isCrmFlagEnabled && linkedLead && leadHref"
      :to="leadHref"
      v-tooltip="`Lead · ${linkedLead.pipeline?.name || ''}${linkedLead.stage?.name ? ' · ' + linkedLead.stage.name : ''}`"
      class="hidden xl:inline-flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 text-xs text-n-slate-11 hover:text-n-slate-12 transition-colors"
    >
      <i class="i-lucide-external-link size-3.5" />
      Abrir detalhes do lead
    </router-link>
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <!-- Canal de entrada da conversa (inbox) - sempre visível aqui. -->
          <InboxName :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />

      <!-- CTA principal: Criar lead (quando não há) OU Qualificar (quando há).
           Fica na mesma linha de "Abrir" e MoreActions - área de ação principal.
           Fallback no contactId: se `currentContact` ainda não carregou no store,
           usa `chat.meta.sender.id` direto (presente desde o primeiro render). -->
      <template v-if="isCrmFlagEnabled && (currentContact?.id || chat?.meta?.sender?.id)">
        <NextButton
          v-if="!linkedLead"
          size="sm"
          color="amber"
          icon="i-lucide-target"
          label="Criar lead"
          v-tooltip="'Categorizar esta conversa como lead (CRM)'"
          @click="openCreateLead"
        />
        <LeadQuickActionsMenu
          v-else
          :lead="linkedLead"
          :account-id="accountId"
          @updated="onLeadUpdated"
        />
      </template>

      <MoreActions :conversation-id="currentChat.id" />
    </div>

    <!-- Dialog inline de criação de lead (CRM gated) -->
    <CreateLeadDialog
      v-if="isCrmFlagEnabled"
      ref="createLeadDialogRef"
      :conversation-id="currentChat.id"
      :contact-id="currentContact?.id || chat?.meta?.sender?.id"
      @created="onLeadCreated"
    />
  </div>
</template>
